/**
 * One URL per wizard step (spec B17).
 *
 * The URL used to stay `/onboarding` for the whole wizard, so no step ever
 * pushed history: browser Back left the app, a reload could only guess where
 * the customer had been, and there was nothing to deep-link to. Each step now
 * has a slug under `/onboarding/`; the bare `/onboarding` stays the entry point
 * every redirect targets, and the page replaces it with the resumed step's URL.
 *
 * Slugs are deliberately not the enum values: `company-information` is an
 * internal id, `company` is what the customer sees in the address bar. The two
 * flows never share a slug, so a URL always names exactly one step.
 */
import { OnboardingSteps } from '../store/steps'

export const ONBOARDING_BASE_PATH = '/onboarding'

const STEP_SLUGS: Record<OnboardingSteps, string> = {
  [OnboardingSteps.Welcome]: 'welcome',
  [OnboardingSteps.Structure]: 'structure',
  [OnboardingSteps.CompanyInformation]: 'company',
  [OnboardingSteps.Shareholders]: 'shareholders',
  [OnboardingSteps.Roles]: 'roles',
  [OnboardingSteps.Summary]: 'summary',
  [OnboardingSteps.Packages]: 'packages',
  [OnboardingSteps.ExistingCompanyName]: 'existing-company',
  [OnboardingSteps.ExistingShareholders]: 'existing-shareholders',
  [OnboardingSteps.ExistingDocuments]: 'existing-documents',
  [OnboardingSteps.ExistingCompanyInformation]: 'existing-information',
}

// A Map, not an object lookup, so `__proto__` and friends can never resolve.
const STEPS_BY_SLUG = new Map<string, OnboardingSteps>(
  (Object.entries(STEP_SLUGS) as [OnboardingSteps, string][]).map(([step, slug]) => [slug, step]),
)

export function onboardingStepPath(step: OnboardingSteps): string {
  return `${ONBOARDING_BASE_PATH}/${STEP_SLUGS[step]}`
}

/** The step a `$step` route param names, or null for a missing or unknown slug. */
export function stepFromSlug(slug: unknown): OnboardingSteps | null {
  const value = Array.isArray(slug) ? slug[0] : slug
  if (typeof value !== 'string') return null
  return STEPS_BY_SLUG.get(value) ?? null
}

/** Is this the wizard: the bare entry or any step under it? */
export function isOnboardingPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return pathname === ONBOARDING_BASE_PATH || pathname.startsWith(`${ONBOARDING_BASE_PATH}/`)
}
